import { Component, EventEmitter, Input, OnInit, Output, AfterContentInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { IDFormatRepresentantiveValidator } from 'src/app/shared/validators/id-format-representative.validator';
import { IDLengthRepresentativeValidator } from 'src/app/shared/validators/id-length-representative.validator';
import { LettersOnlyValidator } from 'src/app/shared/validators/letters-only.validators';
import { RequiredValidator } from 'src/app/shared/validators/required.validator';
import { EmailValidator } from 'src/app/shared/validators/email.validators';
import { Roles } from 'src/app/shared/enums/Roles.enum';
import { LocationService } from './location.service';
import { LaftService } from 'src/app/shared/services/laft.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { MAX_UPLOAD_FILE_SIZE_MB, isBase64FileTooLarge } from 'src/app/shared/utils/file-size.util';

@Component({
  selector: 'app-laft-company-data-form',
  templateUrl: './laft-company-data-form.component.html',
  styleUrls: ['./laft-company-data-form.component.scss'],
})
export class LaftCompanyDataFormComponent implements OnInit, OnChanges, AfterContentInit, OnDestroy {
  @Input() userData: any;
  @Input() initData: any;

  readonly Roles = Roles;

  get role(): Roles {
    return this.userData?.role === Roles.CLIENT ? Roles.CLIENT : Roles.INVESTOR;
  }

  @Output() nextStep = new EventEmitter();
  @Output() formChangesEvent = new EventEmitter();

  public preferredCountries: CountryISO[] = [CountryISO.Peru, CountryISO.Spain];
  public searchCountryField = SearchCountryField;
  public countryISO = CountryISO;
  public phoneNumberFormat = PhoneNumberFormat;
  public phoneNumberMaxDigit = 9;
  public phoneError = '';
  public officePhoneMaxDigit = 9;
  public officePhoneError = '';
  public representativePhoneError = '';
  public representativePhoneMaxDigit = 9;
  public secondRepresentativePhoneError = '';
  public secondRepresentativePhoneMaxDigit = 9;
  public readonly maxUploadFileSizeMb = MAX_UPLOAD_FILE_SIZE_MB;
  public isCopiaLiteralTooLarge = false;

  formSubmitSubject$ = new Subject();
  ngUnsubscribe: Subject<void> = new Subject<void>();

  public companyDataForm: FormGroup;
  departmentOptions;
  provinceOptions;
  districtOptions;
  isDepartmentDisabled = false;
  isProvinceDisabled = true;
  isDistrictDisabled = true;
  PERU = 'Perú';

  public idTypeOptions = [
    { value: 'national id', text: 'DNI' },
    { value: 'foreigner card', text: 'Carnet de Extranjería' },
    { value: 'passport', text: 'Pasaporte' },
  ];
  uniqueDocumentValidator: any;

  public annualIncomeOptions = [
    { value: 'less_than_150uit', text: 'Hasta 150 UIT' },
    { value: '150uit_to_1700uit', text: 'De 150 UIT hasta 1,700 UIT' },
    { value: '1700uit_to_2300uit', text: 'De 1,700 UIT hasta 2,300 UIT' },
    { value: 'more_than_2300uit', text: 'Más de 2,300 UIT' },
  ];

  public averageAmountPerInvoiceOptions = [
    { value: 'less_than_20000', text: 'Menor a 20,000 soles' },
    { value: '20000_to_50000', text: 'Entre 20,000 a 50,000 soles' },
    { value: 'more_than_50000', text: 'Mayor a 50,000 soles' },
  ];

  public declaredAssetsOptions = [
    { value: 'real_estate', text: 'Inmuebles' },
    { value: 'vehicles', text: 'Vehículos' },
    { value: 'machinery', text: 'Maquinaria o equipo registrables' },
    { value: 'other_assets', text: 'Otros bienes registrables' },
    { value: 'no_assets', text: 'No tiene' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private locationService: LocationService,
    private laftService: LaftService,
    private spinner: NgxSpinnerService,
  ) {}

  ngOnInit(): void {
    this.companyDataForm = this.formBuilder.group({
      names: ['', [RequiredValidator, LettersOnlyValidator, Validators.minLength(2), Validators.maxLength(50)]],
      familyNames: ['', [RequiredValidator, LettersOnlyValidator, Validators.minLength(2), Validators.maxLength(50)]],
      companyName: [''],
      idNumber: [''],
      cellphone: ['', [Validators.required]],
      email: [''],
      sector: ['', [Validators.required]],
      economicalActivity: ['', [Validators.required]],
      country: [this.PERU, []],
      department: ['', [Validators.required]],
      province: ['', [Validators.required]],
      district: ['', [Validators.required]],
      address: ['', [Validators.required]],
      officePhone: ['', [Validators.required]],
      copiaLiteral: ['', [Validators.required]],
      representativeNames: [
        '',
        [RequiredValidator, LettersOnlyValidator, Validators.minLength(2), Validators.maxLength(50)],
      ],
      representativeLastNames: [
        '',
        [RequiredValidator, LettersOnlyValidator, Validators.minLength(2), Validators.maxLength(50)],
      ],
      representativeIdType: ['national id', [Validators.required]],
      representativeIdNumber: [
        '',
        [
          RequiredValidator,
          IDFormatRepresentantiveValidator('representativeIdType'),
          IDLengthRepresentativeValidator('representativeIdType'),
        ],
      ],
      representativeEmail: ['', [EmailValidator]],
      representativePhone: ['', [Validators.required]],
      representativeIdFrontPicture: ['', [Validators.required]],
      representativeIdBackPicture: ['', [Validators.required]],
      // recordNumber: ['', [Validators.required]],
      // entryNumber: ['', [Validators.required]],
      // businessSector: ['', [Validators.required]],
      // registryZoneNumber: ['', [Validators.required]],
      requiresJointSignature: [false, [Validators.required]],
      // legalPowerType: ['power', [Validators.required]],
      // legalPublicInstrument: ['escritura_publica', [Validators.required]],
      // notarioNames: ['', [Validators.required]],
      // escrituraPublicaDate: ['', [Validators.required]],
      // certificateCopyDate: ['', []],
      // actaDate: ['', []],
      // otherInstrument: ['', []],
    });

    if (this.role === Roles.CLIENT) {
      this.addClientEconomicControls();
    }

    this.companyDataForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      const cleanedData = this.removeEmptyFields(this.companyDataForm.value);
      this.formChangesEvent.emit(cleanedData);
    });

    this.companyDataForm
      .get('representativeIdType')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(idType => {
        this.updateDocumentValidations(idType);
      });

    // this.companyDataForm
    //   .get('legalPublicInstrument')
    //   ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
    //   .subscribe(type => {
    //     this.updatePublicInstrumentValidations(type);
    //   });

    this.companyDataForm
      .get('requiresJointSignature')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(isChecked => {
        this.updateJoinSignatureValidations(isChecked);
      });

    this.formSubmitSubject$
      .pipe(
        tap(() => {
          this.companyDataForm.markAsDirty();
          this.companyDataForm.markAllAsTouched();
          Object.keys(this.companyDataForm.controls).forEach(key => {
            const control = this.companyDataForm.get(key);
            control?.updateValueAndValidity({ emitEvent: true });
          });
          this.handlePhoneValidation();
          this.handleOfficePhoneValidation();
          this.handleRepresentativePhoneValidation();
          this.handleSecondRepresentativePhoneValidation();
        }),
        switchMap(() =>
          this.companyDataForm.statusChanges.pipe(
            startWith(this.companyDataForm.status),
            filter(status => status !== 'PENDING'),
            take(1)
          )
        ),
        filter(status => status === 'VALID')
      )
      .subscribe(() => {
        const cleanedData = this.removeEmptyFields(this.companyDataForm.value);
        this.nextStep.next(cleanedData);
      });

    this.loadDepartments();
    this.setupLocationCascade();

    // Set country to Peru by default and enable location fields
    this.companyDataForm.patchValue({ country: this.PERU });

    if (this.userData) {
      this.populateFormWithUserData();
    }

    if (this.initData) {
      this.populateFormWithInitData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.companyDataForm) return;
    if (changes.userData && this.userData) {
      this.populateFormWithUserData();
    }
    if (changes.initData && this.initData) {
      this.populateFormWithInitData();
    }
  }

  private addClientEconomicControls(): void {
    this.companyDataForm.addControl(
      'annualIncomeAmount',
      this.formBuilder.control('', [Validators.required])
    );
    this.companyDataForm.addControl(
      'averageAmountPerInvoice',
      this.formBuilder.control('', [Validators.required])
    );
    this.companyDataForm.addControl('declaredAssets', this.formBuilder.control('', [Validators.required]));
    this.companyDataForm.addControl('recordNumber', this.formBuilder.control('', [Validators.required]));
    this.companyDataForm.addControl('registryZoneNumber', this.formBuilder.control('', [Validators.required]));
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onContinue(): void {
    this.formSubmitSubject$.next();
  }

  public get selectedRepresentativeIdType(): string {
    return this.companyDataForm.get('representativeIdType').value || '';
  }

  onCopiaLiteralFileLoaded(event: any): void {
    if (isBase64FileTooLarge(event)) {
      this.isCopiaLiteralTooLarge = true;
      this.companyDataForm.get('copiaLiteral')?.setValue(null);
      return;
    }
    this.isCopiaLiteralTooLarge = false;
  }

  private removeEmptyFields(data: any): any {
    const cleaned = { ...data };

    if (cleaned.officePhone && cleaned.officePhone.number) {
      const phone = cleaned.officePhone;
      cleaned.officePhoneNumber = phone?.number?.replace(/ /g, '');
      cleaned.officePhoneCode = phone?.dialCode;
      cleaned.officePhoneCountry = phone?.countryCode;
    }

    delete cleaned.officePhone;

    if (cleaned.cellphone && cleaned.cellphone.number) {
      const phone = cleaned.cellphone;
      cleaned.phone = phone?.number?.replace(/ /g, '');
      cleaned.phoneCode = phone?.dialCode;
      cleaned.phoneCountry = phone?.countryCode;
    }

    delete cleaned.cellphone;

    if (cleaned.representativePhone && cleaned.representativePhone.number) {
      const phone = cleaned.representativePhone;
      cleaned.representativePhoneNumber = phone?.number?.replace(/ /g, '');
      cleaned.representativePhoneCode = phone?.dialCode;
      cleaned.representativePhoneCountry = phone?.countryCode;
    }
    delete cleaned.representativePhone;

    if (cleaned.secondRepresentativePhone && cleaned.secondRepresentativePhone.number) {
      const phone = cleaned.secondRepresentativePhone;
      cleaned.secondRepresentativePhoneNumber = phone?.number?.replace(/ /g, '');
      cleaned.secondRepresentativePhoneCode = phone?.dialCode;
      cleaned.secondRepresentativePhoneCountry = phone?.countryCode;
    }
    delete cleaned.secondRepresentativePhone;

    // Country is always Peru, so we don't need to check it
    cleaned.country = this.PERU;

    // Form data: companyRuc for client, idNumber for investor
    const documentValue = cleaned.idNumber != null && cleaned.idNumber !== '' ? cleaned.idNumber : null;
    delete cleaned.idNumber;
    if (this.role === Roles.CLIENT && documentValue != null) {
      cleaned.companyRuc = documentValue;
    } else if (this.role === Roles.INVESTOR && documentValue != null) {
      cleaned.idNumber = documentValue;
    }

    if (!cleaned?.representativeIdBackPicture || cleaned?.representativeIdBackPicture?.trim() === '') {
      delete cleaned.representativeIdBackPicture;
    }

    // if (this.role === Roles.CLIENT && cleaned.requiresJointSignature) {
    //   if (cleaned.recordNumber != null && cleaned.recordNumber !== '') {
    //     cleaned.secondRepresentativeRecordNumber = cleaned.recordNumber;
    //   }
    //   if (cleaned.registryZoneNumber != null && cleaned.registryZoneNumber !== '') {
    //     cleaned.secondRepresentativeRegistryZoneNumber = cleaned.registryZoneNumber;
    //   }
    // }

    Object.keys(cleaned).forEach(key => {
      if (
        cleaned[key] === null ||
        cleaned[key] === '' ||
        cleaned[key] === undefined ||
        cleaned[key] === 'Fecha invalida'
      ) {
        delete cleaned[key];
      }
    });

    return cleaned;
  }

  private updateDocumentValidations(idType: string): void {
    const idBackControl = this.companyDataForm.get('representativeIdBackPicture');
    if (idType === 'passport') {
      idBackControl?.clearValidators();
    } else {
      idBackControl?.setValidators([Validators.required]);
    }
    idBackControl?.updateValueAndValidity();
  }

  // private updatePublicInstrumentValidations(type: string): void {
  //   const notarioNamesControl = this.companyDataForm.get('notarioNames');
  //   const escrituraPublicaDateControl = this.companyDataForm.get('escrituraPublicaDate');

  //   const certificateCopyDateControl = this.companyDataForm.get('certificateCopyDate');
  //   const actaDateControl = this.companyDataForm.get('actaDate');

  //   const otherInstrumentControl = this.companyDataForm.get('otherInstrument');

  //   switch (type) {
  //     case 'copia_acta':
  //       notarioNamesControl?.clearValidators();
  //       notarioNamesControl?.reset(null);
  //       escrituraPublicaDateControl?.clearValidators();
  //       escrituraPublicaDateControl?.reset(null);

  //       certificateCopyDateControl?.setValidators(Validators.required);
  //       certificateCopyDateControl?.updateValueAndValidity();
  //       actaDateControl?.setValidators(Validators.required);
  //       actaDateControl?.updateValueAndValidity();

  //       otherInstrumentControl?.clearValidators();
  //       otherInstrumentControl?.reset(null);
  //       break;

  //     case 'otro':
  //       notarioNamesControl?.clearValidators();
  //       notarioNamesControl?.reset(null);
  //       escrituraPublicaDateControl?.clearValidators();
  //       escrituraPublicaDateControl?.reset(null);

  //       certificateCopyDateControl?.clearValidators();
  //       certificateCopyDateControl?.reset(null);
  //       actaDateControl?.clearValidators();
  //       actaDateControl?.reset(null);

  //       otherInstrumentControl?.setValidators(Validators.required);
  //       otherInstrumentControl?.updateValueAndValidity();
  //       break;
  //     default:
  //       notarioNamesControl?.setValidators(Validators.required);
  //       notarioNamesControl?.updateValueAndValidity();
  //       escrituraPublicaDateControl?.setValidators(Validators.required);
  //       escrituraPublicaDateControl?.updateValueAndValidity();

  //       certificateCopyDateControl?.clearValidators();
  //       certificateCopyDateControl?.reset(null);
  //       actaDateControl?.clearValidators();
  //       actaDateControl?.reset(null);

  //       otherInstrumentControl?.clearValidators();
  //       otherInstrumentControl?.reset(null);
  //       break;
  //   }
  // }

  // private updateSecondRepresentativeInstrumentValidations(type: string): void {
  //   const notarioNamesControl = this.companyDataForm.get('secondRepresentativeNotarioNames');
  //   const escrituraPublicaDateControl = this.companyDataForm.get('secondRepresentativeEscrituraPublicaDate');
  //   const certificateCopyDateControl = this.companyDataForm.get('secondRepresentativeCertificateCopyDate');
  //   const actaDateControl = this.companyDataForm.get('secondRepresentativeActaDate');
  //   const otherInstrumentControl = this.companyDataForm.get('secondRepresentativeOtherInstrument');

  //   switch (type) {
  //     case 'copia_acta':
  //       notarioNamesControl?.clearValidators();
  //       notarioNamesControl?.reset(null);
  //       escrituraPublicaDateControl?.clearValidators();
  //       escrituraPublicaDateControl?.reset(null);
  //       certificateCopyDateControl?.setValidators(Validators.required);
  //       certificateCopyDateControl?.updateValueAndValidity();
  //       actaDateControl?.setValidators(Validators.required);
  //       actaDateControl?.updateValueAndValidity();
  //       otherInstrumentControl?.clearValidators();
  //       otherInstrumentControl?.reset(null);
  //       break;
  //     case 'otro':
  //       notarioNamesControl?.clearValidators();
  //       notarioNamesControl?.reset(null);
  //       escrituraPublicaDateControl?.clearValidators();
  //       escrituraPublicaDateControl?.reset(null);
  //       certificateCopyDateControl?.clearValidators();
  //       certificateCopyDateControl?.reset(null);
  //       actaDateControl?.clearValidators();
  //       actaDateControl?.reset(null);
  //       otherInstrumentControl?.setValidators(Validators.required);
  //       otherInstrumentControl?.updateValueAndValidity();
  //       break;
  //     default:
  //       notarioNamesControl?.setValidators(Validators.required);
  //       notarioNamesControl?.updateValueAndValidity();
  //       escrituraPublicaDateControl?.setValidators(Validators.required);
  //       escrituraPublicaDateControl?.updateValueAndValidity();
  //       certificateCopyDateControl?.clearValidators();
  //       certificateCopyDateControl?.reset(null);
  //       actaDateControl?.clearValidators();
  //       actaDateControl?.reset(null);
  //       otherInstrumentControl?.clearValidators();
  //       otherInstrumentControl?.reset(null);
  //       break;
  //   }
  // }

  private updateJoinSignatureValidations(isChecked: boolean): void {
    if (isChecked) {
      this.companyDataForm.addControl(
        'secondRepresentativeNames',
        new FormControl('', [
          RequiredValidator,
          LettersOnlyValidator,
          Validators.minLength(2),
          Validators.maxLength(50),
        ])
      );
      this.companyDataForm.addControl(
        'secondRepresentativeLastNames',
        new FormControl('', [
          RequiredValidator,
          LettersOnlyValidator,
          Validators.minLength(2),
          Validators.maxLength(50),
        ])
      );
      this.companyDataForm.addControl(
        'secondRepresentativeIdType',
        new FormControl('national id', [Validators.required])
      );
      this.companyDataForm.addControl(
        'secondRepresentativeNumber',
        new FormControl('', [
          RequiredValidator,
          IDFormatRepresentantiveValidator('secondRepresentativeIdType'),
          IDLengthRepresentativeValidator('secondRepresentativeIdType'),
        ])
      );
      this.companyDataForm.addControl('secondRepresentativeEmail', new FormControl('', [EmailValidator]));
      this.companyDataForm.addControl('secondRepresentativePhone', new FormControl('', [Validators.required]));

      this.companyDataForm.addControl('secondRepresentativeIdFrontPicture', new FormControl('', Validators.required));
      this.companyDataForm.addControl('secondRepresentativeIdBackPicture', new FormControl('', Validators.required));

      // this.companyDataForm.addControl(
      //   'secondRepresentativeLegalPowerType',
      //   new FormControl('power', Validators.required)
      // );
      // this.companyDataForm.addControl(
      //   'secondRepresentativeLegalPublicInstrument',
      //   new FormControl('escritura_publica', Validators.required)
      // );
      // this.companyDataForm.addControl('secondRepresentativeNotarioNames', new FormControl('', Validators.required));
      // this.companyDataForm.addControl(
      //   'secondRepresentativeEscrituraPublicaDate',
      //   new FormControl('', Validators.required)
      // );
      // this.companyDataForm.addControl('secondRepresentativeCertificateCopyDate', new FormControl('', []));
      // this.companyDataForm.addControl('secondRepresentativeActaDate', new FormControl('', []));
      // this.companyDataForm.addControl('secondRepresentativeOtherInstrument', new FormControl('', []));

      // if (this.role !== Roles.CLIENT) {
      //   this.companyDataForm.addControl(
      //     'secondRepresentativeRecordNumber',
      //     new FormControl('', Validators.required)
      //   );
      //   this.companyDataForm.addControl(
      //     'secondRepresentativeRegistryZoneNumber',
      //     new FormControl('', Validators.required)
      //   );
      // }

      // this.companyDataForm.addControl('secondRepresentativeEntryNumber', new FormControl('', Validators.required));
      // this.companyDataForm.addControl('secondRepresentativeBusinessSector', new FormControl('', Validators.required));

      this.companyDataForm
        .get('secondRepresentativeIdType')
        ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(idType => {
          const idBackControl = this.companyDataForm.get('secondRepresentativeIdBackPicture');
          if (idType === 'passport') {
            idBackControl?.clearValidators();
          } else {
            idBackControl?.setValidators([Validators.required]);
          }
          idBackControl?.updateValueAndValidity();
        });

      // this.companyDataForm
        // .get('secondRepresentativeLegalPublicInstrument')
        // ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
        // .subscribe(type => this.updateSecondRepresentativeInstrumentValidations(type));
    } else {
      this.companyDataForm.get('secondRepresentativeNames')?.reset(null);
      this.companyDataForm.get('secondRepresentativeLastNames')?.reset(null);
      this.companyDataForm.get('secondRepresentativeIdType')?.reset(null);
      this.companyDataForm.get('secondRepresentativeNumber')?.reset(null);
      this.companyDataForm.get('secondRepresentativeEmail')?.reset(null);
      this.companyDataForm.get('secondRepresentativePhone')?.reset(null);
      this.companyDataForm.get('secondRepresentativeIdFrontPicture')?.reset(null);
      this.companyDataForm.get('secondRepresentativeIdBackPicture')?.reset(null);
      // this.companyDataForm.get('secondRepresentativeLegalPowerType')?.reset(null);
      // this.companyDataForm.get('secondRepresentativeLegalPublicInstrument')?.reset(null);
      // this.companyDataForm.get('secondRepresentativeNotarioNames')?.reset(null);
      // this.companyDataForm.get('secondRepresentativeEscrituraPublicaDate')?.reset(null);
      // this.companyDataForm.get('secondRepresentativeCertificateCopyDate')?.reset(null);
      // this.companyDataForm.get('secondRepresentativeActaDate')?.reset(null);
      // this.companyDataForm.get('secondRepresentativeOtherInstrument')?.reset(null);
      // if (this.role !== Roles.CLIENT) {
      //   this.companyDataForm.get('secondRepresentativeRecordNumber')?.reset(null);
      //   this.companyDataForm.removeControl('secondRepresentativeRecordNumber');
      //   this.companyDataForm.get('secondRepresentativeRegistryZoneNumber')?.reset(null);
      //   this.companyDataForm.removeControl('secondRepresentativeRegistryZoneNumber');
      // }
      // this.companyDataForm.get('secondRepresentativeEntryNumber')?.reset(null);
      // this.companyDataForm.get('secondRepresentativeBusinessSector')?.reset(null);

      this.companyDataForm.removeControl('secondRepresentativeNames');
      this.companyDataForm.removeControl('secondRepresentativeLastNames');
      this.companyDataForm.removeControl('secondRepresentativeIdType');
      this.companyDataForm.removeControl('secondRepresentativeNumber');
      this.companyDataForm.removeControl('secondRepresentativeEmail');
      this.companyDataForm.removeControl('secondRepresentativePhone');
      this.companyDataForm.removeControl('secondRepresentativeIdFrontPicture');
      this.companyDataForm.removeControl('secondRepresentativeIdBackPicture');
      // this.companyDataForm.removeControl('secondRepresentativeLegalPowerType');
      // this.companyDataForm.removeControl('secondRepresentativeLegalPublicInstrument');
      // this.companyDataForm.removeControl('secondRepresentativeNotarioNames');
      // this.companyDataForm.removeControl('secondRepresentativeEscrituraPublicaDate');
      // this.companyDataForm.removeControl('secondRepresentativeCertificateCopyDate');
      // this.companyDataForm.removeControl('secondRepresentativeActaDate');
      // this.companyDataForm.removeControl('secondRepresentativeOtherInstrument');
      // this.companyDataForm.removeControl('secondRepresentativeEntryNumber');
      // this.companyDataForm.removeControl('secondRepresentativeBusinessSector');
    }
  }

  focusOutPhone(): void {
    this.handlePhoneValidation();
  }

  private handlePhoneValidation(): void {
    const cellphoneControl = this.companyDataForm.controls?.cellphone;
    if (cellphoneControl?.touched) {
      if (cellphoneControl?.errors?.required) {
        this.phoneError = 'obligatorio';
      } else if (
        cellphoneControl?.errors?.hasOwnProperty('validatePhoneNumber') &&
        !cellphoneControl?.errors?.validatePhoneNumber?.valid
      ) {
        this.phoneError = 'formato incorrecto';
      } else {
        this.phoneError = '';
      }
    }
  }

  countryChange(event: any): void {
    if (event?.dialCode == '51') {
      this.phoneNumberMaxDigit = 9;
    } else {
      this.phoneNumberMaxDigit = 15;
    }
  }

  private populateFormWithUserData(): void {
    const documentValue =
      this.role === Roles.CLIENT
        ? (this.userData.companyRuc ?? '')
        : (this.userData.idNumber ?? '');
    this.companyDataForm.patchValue({
      names: this.userData.names || '',
      familyNames: this.userData.familyNames || '',
      companyName: this.userData.companyName || '',
      idNumber: documentValue ?? '',
      cellphone: this.userData.phone || '',
      email: this.userData.email || '',
    });
  }

  ngAfterContentInit(): void {
    this.companyDataForm
      .get('cellphone')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.handlePhoneValidation();
      });

    this.companyDataForm
      .get('officePhone')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.handleOfficePhoneValidation();
      });

    this.companyDataForm
      .get('representativePhone')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.handleRepresentativePhoneValidation();
      });

    this.companyDataForm
      .get('secondRepresentativePhone')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.handleSecondRepresentativePhoneValidation();
      });
  }

  private loadDepartments(): void {
    this.locationService
      .getDepartments()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(departments => {
        this.departmentOptions = departments.map(dept => ({
          value: dept,
          text: dept,
        }));
      });
  }

  private setupLocationCascade(): void {
    this.companyDataForm
      .get('department')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(department => {
        this.provinceOptions = [];
        this.districtOptions = [];
        this.isProvinceDisabled = true;
        this.isDistrictDisabled = true;

        if (department) {
          const departmentText = this.departmentOptions.find(d => d.value === department)?.text;
          if (departmentText) {
            this.locationService
              .getProvincesByDepartment(departmentText)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(provinces => {
                this.provinceOptions = provinces.map(prov => ({
                  value: prov.toLowerCase().replace(/\s+/g, '_'),
                  text: prov,
                }));
                this.isProvinceDisabled = false;
              });
          }
        }
      });

    this.companyDataForm
      .get('province')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(province => {
        this.districtOptions = [];

        if (province) {
          const department = this.companyDataForm.get('department')?.value;
          const departmentText = this.departmentOptions.find(d => d.value === department)?.text;
          const provinceText = this.provinceOptions.find(p => p.value === province)?.text;

          if (departmentText && provinceText) {
            this.locationService
              .getDistrictsByProvince(departmentText, provinceText)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe(districts => {
                this.districtOptions = districts.map(dist => ({
                  value: dist.toLowerCase().replace(/\s+/g, '_'),
                  text: dist,
                }));
                this.isDistrictDisabled = false;
              });
          }
        }
      });
  }

  focusOutOfficePhone(): void {
    this.handleOfficePhoneValidation();
  }

  private handleOfficePhoneValidation(): void {
    const officePhoneControl = this.companyDataForm.controls?.officePhone;
    if (officePhoneControl?.touched) {
      if (officePhoneControl?.errors?.required) {
        this.officePhoneError = 'obligatorio';
      } else if (
        officePhoneControl?.errors?.hasOwnProperty('validatePhoneNumber') &&
        !officePhoneControl?.errors?.validatePhoneNumber?.valid
      ) {
        this.officePhoneError = 'formato incorrecto';
      } else {
        this.officePhoneError = '';
      }
    }
  }

  officePhoneCountryChange(event: any): void {
    if (event?.dialCode == '51') {
      this.officePhoneMaxDigit = 9;
    } else {
      this.officePhoneMaxDigit = 15;
    }
  }

  private buildPhoneObjectFromInitData(
    phoneNumber: string,
    phoneCode: string,
    phoneCountry: string
  ): { number: string; dialCode: string; countryCode: string } | '' {
    if (!phoneNumber || !phoneCode) return '';
    const dialCode = phoneCode.startsWith('+') ? phoneCode : `+${phoneCode}`;
    return {
      number: String(phoneNumber).trim(),
      dialCode,
      countryCode: (phoneCountry && String(phoneCountry).trim()) || 'PE',
    };
  }

  /**
   * Parche de campos del instrumento público según la opción guardada.
   * Solo rellena los campos que corresponden a la opción (como en jurídica);
   * el resto se deja vacío para no mostrar datos de otra opción al cambiar.
   */
  // private getInstrumentPatchFromInitData(legalPublicInstrument: string): Record<string, string> {
  //   const base = {
  //     notarioNames: '',
  //     escrituraPublicaDate: '',
  //     certificateCopyDate: '',
  //     actaDate: '',
  //     otherInstrument: '',
  //   };
  //   switch (legalPublicInstrument) {
  //     case 'escritura_publica':
  //       return {
  //         ...base,
  //         notarioNames: this.initData?.notarioNames || '',
  //         escrituraPublicaDate: this.initData?.escrituraPublicaDate || '',
  //       };
  //     case 'copia_acta':
  //       return {
  //         ...base,
  //         certificateCopyDate: this.initData?.certificateCopyDate || '',
  //         actaDate: this.initData?.actaDate || '',
  //       };
  //     case 'otro':
  //       return {
  //         ...base,
  //         otherInstrument: this.initData?.otherInstrument || '',
  //       };
  //     default:
  //       return { ...base };
  //   }
  // }

  // private getSecondRepresentativeInstrumentPatchFromInitData(
  //   secondRepresentativeLegalPublicInstrument: string,
  // ): Record<string, string> {
  //   const base = {
  //     secondRepresentativeNotarioNames: '',
  //     secondRepresentativeEscrituraPublicaDate: '',
  //     secondRepresentativeCertificateCopyDate: '',
  //     secondRepresentativeActaDate: '',
  //     secondRepresentativeOtherInstrument: '',
  //   };
  //   switch (secondRepresentativeLegalPublicInstrument) {
  //     case 'escritura_publica':
  //       return {
  //         ...base,
  //         secondRepresentativeNotarioNames: this.initData?.secondRepresentativeNotarioNames || '',
  //         secondRepresentativeEscrituraPublicaDate:
  //           this.initData?.secondRepresentativeEscrituraPublicaDate || '',
  //       };
  //     case 'copia_acta':
  //       return {
  //         ...base,
  //         secondRepresentativeCertificateCopyDate:
  //           this.initData?.secondRepresentativeCertificateCopyDate || '',
  //         secondRepresentativeActaDate: this.initData?.secondRepresentativeActaDate || '',
  //       };
  //     case 'otro':
  //       return {
  //         ...base,
  //         secondRepresentativeOtherInstrument: this.initData?.secondRepresentativeOtherInstrument || '',
  //       };
  //     default:
  //       return { ...base };
  //   }
  // }

  private populateFormWithInitData(): void {
    const requiresJointSignature = this.initData?.requiresJointSignature || false;
    this.companyDataForm.get('requiresJointSignature')?.setValue(requiresJointSignature, { emitEvent: false });

    this.updateJoinSignatureValidations(requiresJointSignature);

    const representativePhoneValue = this.buildPhoneObjectFromInitData(
      this.initData?.representativePhoneNumber,
      this.initData?.representativePhoneCode,
      this.initData?.representativePhoneCountry
    );
    const secondRepresentativePhoneValue = this.buildPhoneObjectFromInitData(
      this.initData?.secondRepresentativePhoneNumber,
      this.initData?.secondRepresentativePhoneCode,
      this.initData?.secondRepresentativePhoneCountry
    );

    const documentValueFromInit =
      this.role === Roles.CLIENT
        ? (this.initData?.companyRuc ?? this.initData?.idNumber)
        : (this.initData?.idNumber ?? this.initData?.companyRuc);
    const patch: Record<string, unknown> = {
      sector: this.initData?.sector || '',
      economicalActivity: this.initData?.economicalActivity || '',
      annualIncomeAmount: this.initData?.annualIncomeAmount || '',
      averageAmountPerInvoice: this.initData?.averageAmountPerInvoice || '',
      declaredAssets: this.initData?.declaredAssets || this.initData?.declaredAssets || '',
      country: this.initData?.country || '',
      department: this.initData?.department || '',
      province: this.initData?.province || '',
      district: this.initData?.district || '',
      address: this.initData?.address || '',
      officePhone: this.initData?.officePhoneNumber || '',
      representativeNames: this.initData?.representativeNames || '',
      representativeLastNames: this.initData?.representativeLastNames || '',
      representativeIdType: this.initData?.representativeIdType || '',
      representativeIdNumber: this.initData?.representativeIdNumber || '',
      representativeEmail: this.initData?.representativeEmail || '',
      representativePhone: representativePhoneValue || '',
      recordNumber: this.initData?.recordNumber || '',
      // entryNumber: this.initData?.entryNumber || '',
      // businessSector: this.initData?.businessSector || '',
      registryZoneNumber: this.initData?.registryZoneNumber || '',
      // legalPowerType: this.initData?.legalPowerType || 'power',
      // legalPublicInstrument: this.initData?.legalPublicInstrument || 'escritura_publica',
      // ...this.getInstrumentPatchFromInitData(this.initData?.legalPublicInstrument || 'escritura_publica'),
      secondRepresentativeNames: this.initData?.secondRepresentativeNames || '',
      secondRepresentativeLastNames: this.initData?.secondRepresentativeLastNames || '',
      secondRepresentativeIdType: this.initData?.secondRepresentativeIdType || '',
      secondRepresentativeNumber: this.initData?.secondRepresentativeNumber || '',
      secondRepresentativeIdFrontPicture: this.initData?.secondRepresentativeIdFrontPicture || '',
      secondRepresentativeIdBackPicture: this.initData?.secondRepresentativeIdBackPicture || '',
      // secondRepresentativeLegalPowerType: this.initData?.secondRepresentativeLegalPowerType || '',
      // secondRepresentativeLegalPublicInstrument: this.initData?.secondRepresentativeLegalPublicInstrument || '',
      // ...this.getSecondRepresentativeInstrumentPatchFromInitData(
      //   this.initData?.secondRepresentativeLegalPublicInstrument || 'escritura_publica',
      // ),
      // secondRepresentativeEntryNumber: this.initData?.secondRepresentativeEntryNumber || '',
      // secondRepresentativeBusinessSector: this.initData?.secondRepresentativeBusinessSector || '',
      secondRepresentativeEmail: this.initData?.secondRepresentativeEmail || '',
      secondRepresentativePhone: secondRepresentativePhoneValue || '',
    };
    // if (this.role !== Roles.CLIENT) {
    //   patch.secondRepresentativeRecordNumber = this.initData?.secondRepresentativeRecordNumber || '';
    //   patch.secondRepresentativeRegistryZoneNumber = this.initData?.secondRepresentativeRegistryZoneNumber || '';
    // }
    if (documentValueFromInit != null && documentValueFromInit !== '') {
      patch.idNumber = documentValueFromInit;
    }
    this.companyDataForm.patchValue(patch);
    // this.updatePublicInstrumentValidations(
    //   this.companyDataForm.get('legalPublicInstrument')?.value || 'escritura_publica',
    // );
    // if (requiresJointSignature) {
    //   const secondInstrument =
    //     this.companyDataForm.get('secondRepresentativeLegalPublicInstrument')?.value ||
    //     'escritura_publica';
    //   this.updateSecondRepresentativeInstrumentValidations(secondInstrument);
    // }
  }

  focusOutRepresentativePhone(): void {
    this.handleRepresentativePhoneValidation();
  }

  private handleRepresentativePhoneValidation(): void {
    const control = this.companyDataForm.get('representativePhone');
    if (control?.touched) {
      if (control?.errors?.required) {
        this.representativePhoneError = 'obligatorio';
      } else if (
        control?.errors?.hasOwnProperty('validatePhoneNumber') &&
        !control?.errors?.validatePhoneNumber?.valid
      ) {
        this.representativePhoneError = 'formato incorrecto';
      } else {
        this.representativePhoneError = '';
      }
    }
  }

  representativePhoneCountryChange(event: any): void {
    if (event?.dialCode === '51') {
      this.representativePhoneMaxDigit = 9;
    } else {
      this.representativePhoneMaxDigit = 15;
    }
  }

  focusOutSecondRepresentativePhone(): void {
    this.handleSecondRepresentativePhoneValidation();
  }

  private handleSecondRepresentativePhoneValidation(): void {
    const control = this.companyDataForm.get('secondRepresentativePhone');
    if (control?.touched) {
      if (control?.errors?.required) {
        this.secondRepresentativePhoneError = 'obligatorio';
      } else if (
        control?.errors?.hasOwnProperty('validatePhoneNumber') &&
        !control?.errors?.validatePhoneNumber?.valid
      ) {
        this.secondRepresentativePhoneError = 'formato incorrecto';
      } else {
        this.secondRepresentativePhoneError = '';
      }
    }
  }

  secondRepresentativePhoneCountryChange(event: any): void {
    if (event?.dialCode === '51') {
      this.secondRepresentativePhoneMaxDigit = 9;
    } else {
      this.secondRepresentativePhoneMaxDigit = 15;
    }
  }
}
